(() => {
  if (customElements.get('rt-ugc-gallery')) return;
  class RTUGCGallery extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('[data-ugc-track]');
      this.cards = [...this.querySelectorAll('[data-ugc-card]')];
      if (!this.track || !this.cards.length) return;
      this.previous = this.querySelector('[data-ugc-prev]');
      this.next = this.querySelector('[data-ugc-next]');
      this.range = this.querySelector('[data-ugc-range]');
      this.pauseButton = this.querySelector('[data-ugc-pause]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches;
      this.motion.addEventListener('change', () => {
        if (!this.motion.matches) return;
        this.paused = true;
        this.cards.forEach(card => this.syncPlayback(card));
        this.updatePauseButton();
      }, { signal });
      this.activeCard = null;
      this.frame = 0;
      this.track.addEventListener('scroll', () => this.schedule(), { passive: true, signal });
      this.previous?.addEventListener('click', () => this.move(-1), { signal });
      this.next?.addEventListener('click', () => this.move(1), { signal });
      this.pauseButton.addEventListener('click', () => {
        this.paused = !this.paused;
        this.cards.forEach(card => { card.userPaused = false; this.syncPlayback(card); });
        this.updatePauseButton();
      }, { signal });
      this.cards.forEach(card => {
        card.classList.remove('rt-ugc-gallery__card--controls', 'rt-ugc-gallery__card--playing');
        card.querySelector('[data-ugc-watch]').hidden = false;
        card.querySelector('[data-ugc-watch]').setAttribute('aria-expanded', 'false');
        card.querySelector('[data-ugc-hide]').hidden = true;
        card.visible = false;
        card.userPaused = false;
        card.querySelector('[data-ugc-watch]').addEventListener('click', () => this.activate(card), { signal });
        card.querySelector('[data-ugc-hide]').addEventListener('click', () => this.hideControls(card, true), { signal });
      });
      this.track.addEventListener('keydown', event => {
        if (event.target.closest('video')) return;
        if (event.key === 'Escape' && this.activeCard) {
          this.hideControls(this.activeCard, true);
          return;
        }
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const card = event.target.closest('[data-ugc-card]');
        const current = card ? this.cards.indexOf(card) : this.closestCard();
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.cards.length - 1 : Math.max(0, Math.min(this.cards.length - 1, current + (event.key === 'ArrowRight' ? 1 : -1)));
        this.scrollToCard(index);
        this.cards[index].querySelector(this.cards[index] === this.activeCard ? '[data-ugc-hide]' : '[data-ugc-watch]').focus({ preventScroll: true });
      }, { signal });
      this.visibility = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const card = entry.target;
          card.visible = entry.isIntersecting && entry.intersectionRatio >= .15;
          if (card.visible) this.ensureVideo(card);
          else if (card === this.activeCard) this.hideControls(card);
          this.syncPlayback(card);
        });
      }, { threshold: [0, .15] });
      this.cards.forEach(card => this.visibility.observe(card));
      this.resize = new ResizeObserver(() => this.schedule());
      this.resize.observe(this.track);
      document.addEventListener('visibilitychange', () => this.cards.forEach(card => this.syncPlayback(card)), { signal });
      this.updatePauseButton();
      this.update();
    }
    ensureVideo(card) {
      if (card.video) return card.video;
      const video = card.querySelector('template').content.querySelector('video').cloneNode(true);
      video.autoplay = false;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;
      video.preload = 'none';
      video.tabIndex = -1;
      video.setAttribute('aria-label', card.dataset.ugcTitle);
      card.video = video;
      video.addEventListener('playing', () => card.classList.add('rt-ugc-gallery__card--playing'), { signal: this.controller.signal });
      video.addEventListener('pause', () => {
        if (card === this.activeCard && card.visible && !this.paused && !document.hidden) card.userPaused = true;
      }, { signal: this.controller.signal });
      video.addEventListener('play', () => { card.userPaused = false; }, { signal: this.controller.signal });
      card.querySelector('[data-ugc-media]').append(video);
      return video;
    }
    syncPlayback(card) {
      const video = card.video;
      if (!video) return;
      if (!card.visible || document.hidden || this.paused || card.userPaused) {
        if (!video.paused) video.pause();
      } else if (video.paused) {
        video.play().catch(() => {});
      }
    }
    activate(card) {
      if (this.activeCard && this.activeCard !== card) this.hideControls(this.activeCard);
      const video = this.ensureVideo(card);
      this.activeCard = card;
      this.paused = false;
      card.userPaused = false;
      card.classList.add('rt-ugc-gallery__card--controls');
      card.querySelector('[data-ugc-watch]').hidden = true;
      card.querySelector('[data-ugc-watch]').setAttribute('aria-expanded', 'true');
      card.querySelector('[data-ugc-hide]').hidden = false;
      video.controls = true;
      video.muted = false;
      video.tabIndex = 0;
      video.focus({ preventScroll: true });
      this.cards.forEach(reel => this.syncPlayback(reel));
      this.updatePauseButton();
    }
    hideControls(card, restoreFocus = false) {
      if (card.video) {
        card.video.muted = true;
        card.video.controls = false;
        card.video.tabIndex = -1;
      }
      card.classList.remove('rt-ugc-gallery__card--controls');
      card.querySelector('[data-ugc-watch]').hidden = false;
      card.querySelector('[data-ugc-watch]').setAttribute('aria-expanded', 'false');
      card.querySelector('[data-ugc-hide]').hidden = true;
      card.userPaused = false;
      if (this.activeCard === card) this.activeCard = null;
      this.syncPlayback(card);
      if (restoreFocus) card.querySelector('[data-ugc-watch]').focus({ preventScroll: true });
    }
    updatePauseButton() {
      this.pauseButton.setAttribute('aria-label', this.paused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel);
      this.pauseButton.querySelector('[data-ugc-pause-icon]').toggleAttribute('hidden', this.paused);
      this.pauseButton.querySelector('[data-ugc-play-icon]').toggleAttribute('hidden', !this.paused);
    }
    schedule() {
      if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
    }
    closestCard() {
      const left = this.track.getBoundingClientRect().left;
      return this.cards.reduce((best, card, i) => Math.abs(card.getBoundingClientRect().left - left) < Math.abs(this.cards[best].getBoundingClientRect().left - left) ? i : best, 0);
    }
    move(direction) {
      const cardWidth = this.cards[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollBy({ left: direction * (cardWidth + gap) * (rtl ? -1 : 1), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    scrollToCard(index) {
      const track = this.track.getBoundingClientRect();
      const card = this.cards[index].getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollBy({ left: rtl ? card.right - track.right : card.left - track.left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    update() {
      const track = this.track.getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const first = this.cards[0].getBoundingClientRect();
      const last = this.cards[this.cards.length - 1].getBoundingClientRect();
      if (this.previous) this.previous.disabled = rtl ? first.right <= track.right + 2 : first.left >= track.left - 2;
      if (this.next) this.next.disabled = rtl ? last.left >= track.left - 2 : last.right <= track.right + 2;
      const visible = this.cards.map((card, i) => {
        const box = card.getBoundingClientRect();
        const width = Math.max(0, Math.min(box.right, track.right) - Math.max(box.left, track.left));
        return width / box.width >= .5 ? i + 1 : null;
      }).filter(Boolean);
      if (this.range && visible.length) this.range.textContent = `${visible[0]}${visible.length > 1 ? '–' + visible[visible.length - 1] : ''} / ${this.cards.length}`;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.visibility?.disconnect();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      this.cards?.forEach(card => {
        if (!card.video) return;
        card.video.pause();
        card.video.removeAttribute('src');
        card.video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        card.video.load();
        card.video.remove();
        card.video = null;
      });
    }
  }
  customElements.define('rt-ugc-gallery', RTUGCGallery);
})();
